/**
 * La cabecera de un grupo de /certificates describe UNA orden que puede tener N
 * certificados. Mostraba los datos del primero, y eso miente apenas la orden es
 * multiproducto: la venta CE-2608-1365 + CE-2608-1366 se anunciaba como
 * "YOOL DEMO 1 / Plan Incendio UF 500" cuando su segunda línea es de BCI.
 *
 * La regla es: la cabecera solo afirma lo COMÚN. Lo que varía entre líneas se
 * enumera (compañías) o se cuenta (productos), nunca se resuelve tomando el
 * primero.
 */

/** Valores distintos de un campo, en orden de aparición y sin vacíos. */
export function distintos(certs, campo) {
  const vistos = [];
  for (const cert of certs || []) {
    const valor = cert?.[campo];
    if (!valor || valor === '—') continue;
    if (!vistos.includes(valor)) vistos.push(valor);
  }
  return vistos;
}

/**
 * El valor común de un campo, o `null` si las líneas no coinciden.
 * `null` es la señal de "no lo afirmes en la cabecera".
 */
export function comun(certs, campo) {
  const valores = distintos(certs, campo);
  return valores.length === 1 ? valores[0] : null;
}

/**
 * Enumera hasta `max` valores y resume el resto como "+N".
 * Con dos compañías caben ambas, que es justo el caso que se veía mal.
 */
export function enumerar(valores, max = 2) {
  if (!valores || valores.length === 0) return null;
  if (valores.length <= max) return valores.join(' · ');
  return `${valores.slice(0, max).join(' · ')} +${valores.length - max}`;
}

/**
 * Lo que la cabecera puede afirmar de un grupo.
 *
 * - `companiasTexto` — todas las distintas, enumeradas. Nunca solo la primera.
 * - `plan`           — el plan común, o null si difiere entre líneas.
 * - `productos`      — los productos distintos que cubre la orden.
 * - `cliente`/`rut`  — comunes, o null (una orden podría cubrir a más de uno).
 * - `estados`        — los estados de cobertura distintos; uno solo si coinciden.
 */
export function resumenGrupo(certs) {
  const lineas = certs || [];
  const companias = distintos(lineas, 'companyName');
  const productos = distintos(lineas, 'productName');
  const planes = distintos(lineas, 'planName');

  return {
    companias,
    companiasTexto: enumerar(companias),
    plan: planes.length === 1 ? planes[0] : null,
    planes,
    productos,
    cliente: comun(lineas, 'clientName'),
    rut: comun(lineas, 'clientRut'),
    estados: distintos(lineas, 'status'),
    // Multicompañía es lo que invalida una cabecera de una sola línea.
    esMulticompania: companias.length > 1,
  };
}
